<template>
  <section
    id="support-campaign"
    v-reveal
    class="v2-support"
  >
    <div class="v2-support__container">
      <!-- Section header — eyebrow + headline + lede. All three are
           admin-editable via the foundation template editor; the
           defaults reproduce Rick's 2026-05-23 mockup verbatim. -->
      <div class="v2-support__header">
        <span class="v2-support__eyebrow">
          <span class="v2-support__eyebrow-dot" aria-hidden="true"></span>
          {{ eyebrowText }}
        </span>
        <h2 class="v2-support__title">{{ headlineText }}</h2>
        <div
          v-if="introHtml"
          class="v2-support__intro"
          v-html="introHtml"
        ></div>
      </div>

      <!-- Two cards: Fundraise (blue accent) + Share (pink/purple accent).
           grid-template-columns flips to 1fr at mobile. -->
      <div class="v2-support__cards">
        <!-- ── Fundraise ─────────────────────────────────────── -->
        <article class="v2-support__card v2-support__card--fundraise">
          <div class="v2-support__card-head">
            <div class="v2-support__card-icon v2-support__card-icon--blue" aria-hidden="true">
              <!-- Lucide rocket -->
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
                <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
              </svg>
            </div>
            <div class="v2-support__card-titles">
              <h3 class="v2-support__card-title">Become a Team Fundraiser</h3>
              <p class="v2-support__card-sub">Rally your circle behind the team — and the patients Side-Out fights for.</p>
            </div>
          </div>

          <!-- "Your fundraising page" preview — the visual artifact that mirrors
               the Share card's QR panel, so the two cards balance by composition
               (head → artifact → supporting → action) rather than by stretched
               whitespace. Illustrative sample (not real data) → aria-hidden. -->
          <div class="v2-support__page-preview" aria-hidden="true">
            <div class="v2-support__pp-avatar">JM</div>
            <div class="v2-support__pp-body">
              <span class="v2-support__pp-eyebrow">Your fundraising page</span>
              <span class="v2-support__pp-name">Jordan's Dig Pink Page</span>
              <div class="v2-support__pp-bar"><div class="v2-support__pp-fill"></div></div>
              <div class="v2-support__pp-stats">
                <span class="v2-support__pp-raised">$340 raised</span>
                <span class="v2-support__pp-goal">Goal $750</span>
              </div>
            </div>
          </div>

          <ul class="v2-support__features">
            <li>
              <span class="v2-support__check v2-support__check--blue" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <span>
                <strong>Set up in under 2 minutes</strong>
                <span class="v2-support__feature-desc">A guided flow gets you a personal fundraising page</span>
              </span>
            </li>
            <li>
              <span class="v2-support__check v2-support__check--blue" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <span>
                <strong>Tell people why this matters to you</strong>
                <span class="v2-support__feature-desc">Add your photo, your story, and your goal</span>
              </span>
            </li>
            <li>
              <span class="v2-support__check v2-support__check--blue" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <span>
                <strong>Every dollar fuels the mission</strong>
                <span class="v2-support__feature-desc">Funds roll up to our team total and into Side-Out research</span>
              </span>
            </li>
          </ul>

          <button
            type="button"
            class="v2-support__cta v2-support__cta--blue"
            @click="openSupportModal"
          >
            {{ ctaLabel }}
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </button>
          <V2RegistrationLink v-if="campaign?.id" :campaign-id="campaign.id" />
        </article>

        <!-- ── Share ─────────────────────────────────────────── -->
        <article class="v2-support__card v2-support__card--share">
          <div class="v2-support__card-head">
            <div class="v2-support__card-icon v2-support__card-icon--pink" aria-hidden="true">
              <!-- Lucide megaphone -->
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m3 11 18-5v12L3 14v-3z"/>
                <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
              </svg>
            </div>
            <div class="v2-support__card-titles">
              <h3 class="v2-support__card-title">Spread the Word, Spread the Mission</h3>
              <p class="v2-support__card-sub">Every share puts the team — and Side-Out's fight — in front of someone new.</p>
            </div>
          </div>

          <!-- QR-driven sharing panel — big code on the left, "show this
               code" copy on the right. On mobile, both stack centered. -->
          <div class="v2-support__qr-panel">
            <div class="v2-support__qr" v-if="qrSvg" v-html="qrSvg" aria-label="QR code for this campaign" role="img"></div>
            <div v-else class="v2-support__qr v2-support__qr--placeholder" aria-hidden="true"></div>
            <div class="v2-support__qr-copy">
              <div class="v2-support__qr-label">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
                Sharing in person?
              </div>
              <h4>Just show this code</h4>
              <!-- Restored 2026-06-11 (Rick) — supporting copy under the QR
                   heading was dropped in #146; the .v2-support__qr-copy p style
                   was left in place, so it picks up its original treatment. -->
              <p>Point any phone camera at this QR code — it opens our campaign page instantly. Perfect for tabling events, games, and conversations on the go.</p>
            </div>
          </div>

          <div class="v2-support__divider">— or share online —</div>

          <div class="v2-support__copy-row">
            <input
              ref="linkInput"
              type="text"
              class="v2-support__url"
              :value="shareUrl"
              readonly
              aria-label="Campaign link"
              @focus="$event.target.select()"
            />
            <button
              type="button"
              class="v2-support__copy-btn"
              :class="{ 'is-copied': justCopied }"
              @click="copyLink"
            >
              {{ justCopied ? 'Copied!' : 'Copy Link' }}
            </button>
          </div>

          <div class="v2-support__social-grid">
            <button
              v-for="opt in shareOptions"
              :key="opt.key"
              type="button"
              :class="['v2-support__social', 'v2-support__social--' + opt.key]"
              @click="openShare(opt)"
            >
              <span class="v2-support__social-icon" aria-hidden="true" v-html="opt.icon"></span>
              <span>{{ opt.label }}</span>
            </button>
          </div>

          <!-- The native-share "Share this campaign" CTA was removed here
               (Rick 2026-06-11) — the copy-link row + social grid above are the
               complete share surface, so the extra OS-share button was
               redundant. The Share card now closes on the social grid; the
               cards stay balanced via .v2-support__cards align-items:stretch
               with the social grid pushed to the bottom (margin-top:auto). -->
        </article>
      </div>

      <!-- Mission strip — navy gradient panel summarizing why "any form" of
           support matters. Hardcoded copy across campaigns (brand-level
           message); admin editor doesn't expose this yet. -->
      <div class="v2-support__mission">
        <span class="v2-support__mission-heart" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="#ff7eb6" stroke="#ff7eb6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
          </svg>
        </span>
        <p>
          <strong>We play for the ones who can't wait.</strong>
          Your support — in any form — helps Side-Out Foundation fund the research that gives metastatic breast cancer patients more time.
        </p>
      </div>
    </div>
  </section>
</template>

<script>
// Share-option construction, device gating (SMS hidden on desktop), native
// share, and robust link-opening all live in the canonical useCampaignShare
// module so this inline SUPPORT panel and V2ShareModal never drift apart —
// same icons, same order, same behavior.
import { buildShareOptions, cleanCampaignShareUrl, openShareOption, isDesktop } from '../../../../composables/useCampaignShare'
import V2RegistrationLink from './V2RegistrationLink.vue'

export default {
  name: 'V2SupportOnboarding',
  components: { V2RegistrationLink },
  props: {
    /**
     * Foundation template content. We honor `eyebrow`, `headline`,
     * `intro`, and `cta_label`. The legacy `steps` array is ignored
     * — the redesigned section replaces the 3-step explainer with a
     * two-card Fundraise/Share grid.
     */
    supportOnboarding: { type: Object, default: () => ({}) },
    /**
     * Current campaign. We need the id to build a tracking QR URL
     * (`/qr/{id}` redirect) and the name for the email share template.
     */
    campaign: { type: Object, default: null },
  },
  data() {
    return {
      shareUrl: '',
      qrUrl: '',
      qrSvg: '',
      justCopied: false,
      _copyTimeout: null,
    }
  },
  computed: {
    eyebrowText() {
      return this.supportOnboarding?.eyebrow || 'Other Ways to Support the Fight'
    },
    headlineText() {
      return this.supportOnboarding?.headline || "You Don't Need a Wallet to Make an Impact"
    },
    introHtml() {
      // Honor admin HTML; default copy includes a <strong> emphasis on
      // the foundation name to anchor the mission frame.
      return this.supportOnboarding?.intro
        || 'Every page you start and every share you send helps our team — and fuels <strong>Side-Out Foundation\'s mission</strong> to extend the lives of patients with metastatic breast cancer.'
    },
    ctaLabel() {
      return this.supportOnboarding?.cta_label || 'Start My Fundraiser'
    },
    // Canonical, ordered share set from the shared module:
    // facebook, twitter, linkedin, whatsapp, email, [sms only on mobile],
    // instagram, copy. SMS is dropped on desktop (no Messages app to hand off
    // to — Rick's 2026-06-05 request); copy is a copy-action; instagram copies
    // the link AND opens Instagram (Rick 2026-06-11).
    shareOptions() {
      // Drop the trailing "Copy Link" tile (Rick 2026-06-06: "remove the
      // crossed-out one") — the dedicated Copy Link button in the URL row
      // above already owns that affordance, so the grid tile was redundant.
      return buildShareOptions({
        url: this.shareUrl,
        campaignName: this.campaign?.name,
        desktop: isDesktop(),
      }).filter(opt => opt.key !== 'copy')
    },
  },
  mounted() {
    // Snapshot the URL at mount so route-changes don't mutate it mid-share.
    // Campaign shares must not inherit the arrival link's modal intent
    // (`register=1` / `support=1`).
    this.shareUrl = cleanCampaignShareUrl()
    this.qrUrl = (typeof window !== 'undefined' && this.campaign?.id)
      ? `${window.location.origin}/qr/${this.campaign.id}`
      : this.shareUrl
    this._generateQr()
  },
  beforeUnmount() {
    clearTimeout(this._copyTimeout)
  },
  methods: {
    async _generateQr() {
      try {
        // Dynamic import keeps the qrcode lib out of the main bundle —
        // the lazy chunk loads after first paint when the browser is idle.
        const QRCode = (await import('qrcode')).default
        this.qrSvg = await QRCode.toString(this.qrUrl || this.shareUrl, {
          type: 'svg',
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 170,
          color: { dark: '#1D2549', light: '#ffffff' },
        })
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('V2SupportOnboarding: QR generation failed', err)
        this.qrSvg = ''
      }
    },
    openSupportModal() {
      this.emitter.emit('open-support-modal')
    },
    async copyLink() {
      const url = this.shareUrl
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url)
        } else {
          const input = this.$refs.linkInput
          input.select()
          input.setSelectionRange(0, url.length)
          document.execCommand('copy')
        }
        this.justCopied = true
        clearTimeout(this._copyTimeout)
        this._copyTimeout = setTimeout(() => { this.justCopied = false }, 2000)
      } catch (_e) {
        if (this.$refs.linkInput) this.$refs.linkInput.select()
      }
    },
    openShare(opt) {
      // Delegate to the shared opener: copy-actions (copy) reuse our copyLink
      // (which flips the "Copied!" state + reselects the input on failure);
      // instagram copies the link then opens Instagram in a new tab; popup
      // intents (FB, X, LinkedIn, WhatsApp) open centered and fall back to
      // copyLink if the browser blocks the popup; mailto:/sms: navigate the
      // current document to hand off to the OS handler.
      openShareOption(opt, { onCopy: () => this.copyLink() })
    },
  },
}
</script>

<style scoped>
/* ─── Section frame ───────────────────────────────────────── */
/* Rail-width tokens (1400px container, stepped inline padding) mirror
   V2Hero / V2About / V2ResearchModel / V2RecentActivity / TopHeaderV2,
   so the section's content aligns with every other V2 row at the same
   left + right edge. Rick's mockup used 1280px which read as a visible
   breach of the v2 grid; matching the existing rail keeps the page
   feeling like one design. */
.v2-support {
  background: #ffffff;
  padding-block: 60px;
}
@media (min-width: 768px)  { .v2-support { padding-block: 80px; } }
@media (min-width: 1024px) { .v2-support { padding-block: 96px; } }

.v2-support__container {
  max-width: 1400px;
  margin-inline: auto;
  padding-inline: 24px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  color: var(--sideout-navy);
}
@media (min-width: 768px)  { .v2-support__container { padding-inline: 48px; } }
@media (min-width: 1024px) { .v2-support__container { padding-inline: 64px; } }

/* ─── Header ──────────────────────────────────────────────── */
.v2-support__header {
  text-align: center;
  margin-bottom: 40px;
}
.v2-support__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--sideout-pink);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.v2-support__eyebrow-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--sideout-pink);
  box-shadow: 0 0 0 4px rgba(218, 24, 132, 0.12);
}
.v2-support__title {
  margin: 0 0 14px;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.01em;
  text-wrap: balance;
  color: var(--sideout-navy);
}
@media (min-width: 768px) {
  .v2-support__title { font-size: 42px; }
}
.v2-support__intro {
  margin: 0 auto;
  max-width: 680px;
  color: var(--sideout-muted);
  font-size: 16px;
  line-height: 1.55;
}
.v2-support__intro :deep(p) { margin: 0; }
.v2-support__intro :deep(strong) { color: var(--sideout-navy); font-weight: 700; }

/* ─── Two-card grid ───────────────────────────────────────── */
.v2-support__cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-bottom: 24px;
  /* Equal-height cards on desktop (Rick: "the left and right containers need to
     be the same size"). The shorter Fundraise card stretches to match the taller
     Share card; its feature list is grown + vertically centered (below) so the
     extra height is absorbed evenly instead of opening one empty gap above the
     CTA — the problem the earlier top-aligned version was avoiding. On mobile
     (single-column grid) each card is alone in its row, so this is a no-op. */
  align-items: stretch;
}
@media (min-width: 860px) {
  .v2-support__cards { grid-template-columns: 1fr 1fr; }
}

.v2-support__card {
  background: #F5F6FA;
  border-radius: 16px;
  padding: 36px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
/* Accent strip on each card. Gradients here are decorative
   chrome (4px top edge), not button surfaces — that's why
   they're allowed despite the V2 "no gradients on buttons" rule. */
.v2-support__card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}
.v2-support__card--fundraise::before { background: #009CDE; }
.v2-support__card--share::before     { background: linear-gradient(90deg, var(--sideout-pink), #7C4DFF); }

@media (max-width: 860px) {
  .v2-support__card { padding: 28px 24px; }
}

/* ─── Card head ───────────────────────────────────────────── */
.v2-support__card-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 22px;
}
.v2-support__card-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.v2-support__card-icon svg { width: 28px; height: 28px; }
.v2-support__card-icon--blue { background: #E8F6FD; color: #009CDE; }
/* Rick 2026-06-11: "make this SVG our brand pink" + match the rocket icon's
   exact style — was a purple (#7C4DFF) megaphone on white w/ a border. Now an
   identical structure to --blue above (tinted tile + brand glyph, NO border):
   light-pink tile + brand-pink megaphone. */
.v2-support__card-icon--pink { background: #FCE7F1; color: var(--sideout-pink); }
.v2-support__card-titles { min-width: 0; }
.v2-support__card-title {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.005em;
  color: var(--sideout-navy);
}
.v2-support__card-sub {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: var(--sideout-muted);
}

/* ─── "Your fundraising page" preview (Fundraise card) ──────
   The visual artifact that mirrors the Share card's QR panel — same
   white / 1px-border / 12px-radius / no-shadow chrome — so the two cards
   balance by composition. Illustrative sample, not live data. */
.v2-support__page-preview {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px;
  align-items: center;
  margin-bottom: 22px;
  background: #ffffff;
  border: 1px solid #E2E6F0;
  border-radius: 12px;
  padding: 18px;
}
.v2-support__pp-avatar {
  width: 56px;
  height: 56px;
  /* Rick 2026-06-06: "make this our pink and use the same format as the
     rocket" — adopt the rocket icon-tile treatment (rounded square + tinted
     fill + brand-color glyph) in brand pink instead of a blue avatar circle. */
  border-radius: 14px;
  background: #FCE7F1;            /* light pink tint (mirrors rocket's #E8F6FD) */
  color: #DA1884;                /* brand pink — sass/_variables.scss */
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
  flex-shrink: 0;
  outline: 1px solid #F6D9E7;
}
.v2-support__pp-body { min-width: 0; }
.v2-support__pp-eyebrow {
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--sideout-pink);
  margin-bottom: 4px;
}
.v2-support__pp-name {
  display: block;
  font-size: 16px;
  font-weight: 800;
  color: var(--sideout-navy);
  line-height: 1.1;
  margin-bottom: 9px;
}
.v2-support__pp-bar {
  height: 7px;
  border-radius: 9999px;
  background: #E8EAF0;
  overflow: hidden;
  margin-bottom: 7px;
}
.v2-support__pp-fill {
  height: 100%;
  width: 46%;
  /* Rick 2026-06-06: "make our bar our status bar green." Brand green —
     sass/_variables.scss:$green. The fill sits on the light #E8EAF0 track so
     the lime reads clearly without the legibility concerns it has as text. */
  background: #97D700;
  border-radius: 9999px;
}
.v2-support__pp-stats {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}
.v2-support__pp-raised { font-weight: 800; color: var(--sideout-navy); }
.v2-support__pp-goal { color: var(--sideout-muted); }

/* ─── Fundraise feature list ─────────────────────────────── */
.v2-support__features {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  font-size: 15px;
  color: #3A4660;
}
/* On the equal-height desktop layout the Fundraise card is taller than its
   content; let the feature list grow and center its rows so the slack is
   absorbed evenly (no void above the CTA). On mobile the card isn't stretched,
   so flex:1 has no extra space to fill and the list renders normally. */
.v2-support__card--fundraise .v2-support__features {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.v2-support__features li {
  padding: 12px 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border-bottom: 1px solid #E8EAF0;
}
.v2-support__features li:last-child { border-bottom: none; }
.v2-support__features strong {
  display: block;
  margin-bottom: 2px;
  font-weight: 700;
  color: var(--sideout-navy);
}
.v2-support__feature-desc {
  display: block;
  font-size: 13px;
  line-height: 1.4;
  color: var(--sideout-muted);
}
.v2-support__check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  flex-shrink: 0;
  margin-top: 1px;
}
.v2-support__check--blue { background: #009CDE; }

/* ─── Card CTAs ───────────────────────────────────────────── */
.v2-support__cta {
  /* The feature list above grows to fill the equal-height card (flex:1 + centered
     rows), so the CTA settles at the bottom without the old single gap — the
     slack is absorbed evenly into the list rather than dumped above the button. */
  /* Full column width (align-self: stretch — the card's default; this used to
     override it with flex-start) so the blue "Support Our Campaign" button
     mirrors the pink full-width "Share this campaign" button in the Share card.
     The two primary CTAs now read as a matched pair across the columns. Rick
     2026-06-08: "line up the blue and pink buttons … make the SUPPORT OUR
     CAMPAIGN button full column width to match up." Label stays centered via
     justify-content below, matching the pink button. */
  margin-top: 8px;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 9999px;
  border: 0;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;
}
.v2-support__cta--blue { background: #009CDE; }
.v2-support__cta--blue:hover { background: #0075A7; transform: translateY(-1px); }
.v2-support__cta--blue:active { transform: translateY(0); }
.v2-support__cta:focus-visible {
  outline: 2px solid rgba(0, 156, 222, 0.4);
  outline-offset: 3px;
}

/* ─── Share card: QR panel ────────────────────────────────── */
.v2-support__qr-panel {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
  margin-bottom: 20px;
  background: #ffffff;
  border: 1px solid #EFE3EC;
  border-radius: 12px;
  padding: 20px;
}
.v2-support__qr {
  width: 170px;
  height: 170px;
  padding: 8px;
  background: #ffffff;
  border-radius: 8px;
  outline: 1px solid var(--sideout-border, #E8EAF0);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.v2-support__qr :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}
/* Placeholder while qrcode lib loads — keeps layout stable so the
   QR doesn't pop in and reflow the rest of the card. */
.v2-support__qr--placeholder {
  background: repeating-linear-gradient(45deg, #E8EAF0, #E8EAF0 5px, #ffffff 5px, #ffffff 10px);
}
.v2-support__qr-copy { min-width: 0; }
.v2-support__qr-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: var(--sideout-pink);
  text-transform: uppercase;
  margin-bottom: 6px;
}
.v2-support__qr-label svg { width: 14px; height: 14px; }
.v2-support__qr-copy h4 {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 800;
  color: var(--sideout-navy);
  line-height: 1.2;
}
.v2-support__qr-copy p {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--sideout-muted);
}

/* ─── Divider ─────────────────────────────────────────────── */
.v2-support__divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  color: var(--sideout-muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.v2-support__divider::before,
.v2-support__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #DDE1EA;
}

/* ─── Copy-link row + social grid ────────────────────────── */
.v2-support__copy-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}
.v2-support__url {
  flex: 1 1 0;
  padding: 11px 14px;
  background: #ffffff;
  border: 1px solid var(--sideout-border, #E8EAF0);
  border-radius: 8px;
  font-family: 'Red Hat Text', 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px;
  color: var(--sideout-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  outline: none;
  min-width: 0;
}
.v2-support__url:focus {
  border-color: var(--sideout-pink);
  color: var(--sideout-navy);
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.12);
}
.v2-support__copy-btn {
  padding: 11px 18px;
  background: var(--sideout-pink);
  color: #ffffff;
  border: 0;
  border-radius: 8px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.v2-support__copy-btn:hover { background: var(--sideout-pink-hover, #BC0E65); }
.v2-support__copy-btn.is-copied { background: #16A34A; }
.v2-support__copy-btn:focus-visible {
  outline: 2px solid rgba(218, 24, 132, 0.4);
  outline-offset: 2px;
}

/* 3-up compact tiles (Rick 2026-06-06: "reduce the button size and put 3 in a
   row"). Each tile stacks its icon over a short label so three fit cleanly per
   row at every width — the share card is ~half the section on desktop (3 cols ≈
   190px) and full-width on mobile (3 cols ≈ 100px); the stacked layout keeps the
   label on one line in both. With the redundant Copy Link tile removed in
   shareOptions(), desktop shows a tidy 3×2 (FB · X · LinkedIn / WhatsApp ·
   Email · Instagram). Flex-wrap + justify-content:center (rather than a rigid
   grid) so the mobile-only SMS tile — which makes 7 — leaves a *centered*
   orphan on the last row instead of one stranded in column 1. */
.v2-support__social-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  /* Now the Share card's closing element (the native-share button was removed
     2026-06-11). margin-top:auto anchors it to the card's bottom edge so it
     bottom-aligns with the Fundraise card's "Support Our Campaign" CTA when the
     equal-height grid (align-items:stretch) stretches the shorter card. */
  margin-top: auto;
}
.v2-support__social {
  /* 3 per row: each tile is a third of the row minus the two 8px gaps. */
  flex: 0 0 calc((100% - 16px) / 3);
  padding: 12px 6px;
  background: #ffffff;
  border: 1px solid var(--sideout-border, #E8EAF0);
  border-radius: 8px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--sideout-navy);
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.v2-support__social:hover { background: #FAFBFD; border-color: #C8CDE0; }
.v2-support__social-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.v2-support__social-icon :deep(svg) { width: 20px; height: 20px; }
/* Platform-tinted hover, mirroring V2ShareModal so the inline panel
   feels consistent with the modal experience that lives in the header. */
.v2-support__social--facebook:hover { background: #1877F2; border-color: #1877F2; color: #ffffff; }
.v2-support__social--twitter:hover  { background: #000000; border-color: #000000; color: #ffffff; }
.v2-support__social--linkedin:hover { background: #0A66C2; border-color: #0A66C2; color: #ffffff; }
.v2-support__social--email:hover    { background: var(--sideout-pink); border-color: var(--sideout-pink); color: #ffffff; }
.v2-support__social--sms:hover      { background: #16A34A; border-color: #16A34A; color: #ffffff; }
.v2-support__social--instagram:hover{ background: #E1306C; border-color: #E1306C; color: #ffffff; }
/* Per Rick: show each platform's brand color on the icon at rest (not only on
   hover). On hover the whole button fills with the brand color and text/icon go
   white, so the :not(:hover) guard yields to the hover rule's white color. */
.v2-support__social--facebook:not(:hover)  .v2-support__social-icon { color: #1877F2; }
.v2-support__social--twitter:not(:hover)   .v2-support__social-icon { color: #000000; }
.v2-support__social--linkedin:not(:hover)  .v2-support__social-icon { color: #0A66C2; }
.v2-support__social--email:not(:hover)     .v2-support__social-icon { color: var(--sideout-pink); }
.v2-support__social--sms:not(:hover)       .v2-support__social-icon { color: #16A34A; }
.v2-support__social--instagram:not(:hover) .v2-support__social-icon { color: #E1306C; }

/* ─── Mobile share-panel reflow ──────────────────────────── */
@media (max-width: 860px) {
  .v2-support__qr-panel {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 18px;
  }
  .v2-support__qr { margin: 0 auto; }
  .v2-support__qr-label { justify-content: center; }
  .v2-support__copy-row { flex-direction: column; gap: 8px; }
  .v2-support__copy-btn { width: 100%; }
}

/* iOS auto-zoom guard — inputs <16px trigger zoom-on-focus and reflow
   the rest of the page. Bump to 16px at mobile while still trimming
   long URLs via ellipsis. */
@media (max-width: 640px) {
  .v2-support__url { font-size: 16px; }
}

/* Mobile font bump — client feedback 2026-05-29 (surgical). This section is
   text-dense with several sub-16px readable bodies; nudge the genuinely-small
   *content* up at the phone breakpoint. Headings, the tracked eyebrow, QR /
   divider micro-labels, the bold feature headings, and button labels
   (CTA / copy / social) are intentionally compact and left as-is. */
@media (max-width: 640px) {
  .v2-support__intro        { font-size: 18px; } /* 16 → 18 (section lede) */
  .v2-support__card-sub     { font-size: 16px; } /* 14 → 16 (card subtitle) */
  .v2-support__feature-desc { font-size: 15px; } /* 13 → 15 (feature descriptions) */
  .v2-support__qr-copy p    { font-size: 15px; } /* 13 → 15 (QR-panel description) */
}

/* ─── Mission strip ──────────────────────────────────────── */
.v2-support__mission {
  background: linear-gradient(135deg, var(--sideout-navy) 0%, #2A3556 100%);
  border-radius: 16px;
  padding: 24px 32px;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  justify-content: center;
  text-align: center;
}
.v2-support__mission-heart {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.v2-support__mission p {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  max-width: 720px;
}
.v2-support__mission strong { color: #FF7EB6; font-weight: 700; }
@media (max-width: 860px) {
  .v2-support__mission { padding: 20px; font-size: 14px; }
}

@media (prefers-reduced-motion: reduce) {
  .v2-support__cta,
  .v2-support__copy-btn,
  .v2-support__social { transition-duration: 80ms; }
  .v2-support__cta--blue:hover { transform: none; }
}
</style>
